// ============================================
// Manor Miser - Results Scene (native resolution)
// ============================================

import Phaser from 'phaser';
import { socketManager } from '../network/SocketManager';
import { UIButton } from '../ui/UIButton';
import type { GameOverResult } from '../shared/types';

export class ResultsScene extends Phaser.Scene {
  private result: GameOverResult | null = null;

  constructor() {
    super({ key: 'ResultsScene' });
  }

  create(): void {
    const { width, height } = this.cameras.main;

    this.result = this.registry.get('gameResult');
    const playerId = this.registry.get('playerId');
    const isWinner = this.result?.winnerId === playerId;

    // Background
    const bg = this.add.graphics();
    bg.fillStyle(0x1a1412, 1);
    bg.fillRect(0, 0, width, height);

    // Title
    if (isWinner) {
      const title = this.add.bitmapText(width / 2, 4, 'oldtome', 'Victory!');
      title.setOrigin(0.5, 0).setScale(1).setTint(0xd4a855);
    } else {
      const title = this.add.bitmapText(width / 2, 4, 'oldtome', 'Game Over');
      title.setOrigin(0.5, 0).setScale(1).setTint(0x8a7a6a);

      if (this.result) {
        const winner = this.add.bitmapText(width / 2, 28, 'oldtome', `${this.result.winnerName} Wins!`);
        winner.setOrigin(0.5, 0).setScale(1).setTint(0xd4a855);
      }
    }

    // Round history table
    this.createRoundTable();

    // Buttons
    const buttonY = height - 16;
    const isSinglePlayer = this.registry.get('isSinglePlayer') === true;

    const playAgainBtn = new UIButton(this, width / 2 - 36, buttonY, 'AGAIN (5)', () => {
      socketManager.playAgain();
    }, 60, 16);
    playAgainBtn.setDisabled(true);

    let remaining = 5;
    const countdown = this.time.addEvent({
      delay: 1000,
      repeat: 4,
      callback: () => {
        remaining--;
        if (remaining > 0) {
          playAgainBtn.setText(`AGAIN (${remaining})`);
        } else {
          playAgainBtn.setText('PLAY AGAIN');
          playAgainBtn.setDisabled(false);
          countdown.remove();
        }
      },
    });

    new UIButton(this, width / 2 + 36, buttonY, isSinglePlayer ? 'MENU' : 'LEAVE', () => {
      if (isSinglePlayer) {
        socketManager.returnToLobby();
      } else {
        socketManager.leaveRoom();
      }
      this.registry.set('isSinglePlayer', false);
      this.registry.set('players', null);
      this.registry.set('gameState', null);
      this.registry.set('gameResult', null);
      this.registry.set('yourMoney', null);
      this.cameras.main.fadeOut(300, 26, 20, 18);
      this.time.delayedCall(300, () => {
        this.scene.start('MenuScene');
      });
    }, 60, 16);

    this.setupSocketCallbacks();
    this.cameras.main.fadeIn(500, 26, 20, 18);

    if (isWinner) {
      this.createConfetti();
    }
  }

  private createRoundTable(): void {
    const { width } = this.cameras.main;
    if (!this.result || !this.result.roundHistory?.length) return;

    const history = this.result.roundHistory;
    const playerId = this.registry.get('playerId');

    // Collect all unique players in order of first appearance
    const allPlayerIds: string[] = [];
    const playerNames: Map<string, string> = new Map();
    for (const round of history) {
      for (const bet of round.bets) {
        if (!playerNames.has(bet.playerId)) {
          allPlayerIds.push(bet.playerId);
          playerNames.set(bet.playerId, bet.playerName);
        }
      }
    }

    // Table layout
    const tableTop = 54;
    const rowHeight = 8;
    const nameColWidth = 44;
    const roundColWidth = Math.min(18, (width - nameColWidth - 20) / history.length);
    const tableLeft = Math.max(8, (width - nameColWidth - roundColWidth * history.length) / 2);

    // Header row
    const headerLabel = this.add.bitmapText(tableLeft, tableTop, 'pixel', 'NAME');
    headerLabel.setScale(1).setTint(0x7a6a5a);

    history.forEach((round, colIdx) => {
      const x = tableLeft + nameColWidth + colIdx * roundColWidth;
      const label = this.add.bitmapText(x + roundColWidth / 2, tableTop, 'pixel', `R${round.roundNumber}`);
      label.setOrigin(0.5, 0).setScale(1).setTint(0x7a6a5a);
    });

    // Divider line under header
    const divider = this.add.graphics();
    divider.lineStyle(1, 0x4a3728);
    divider.lineBetween(tableLeft, tableTop + 7, tableLeft + nameColWidth + history.length * roundColWidth, tableTop + 7);

    // Player rows
    allPlayerIds.forEach((pid, rowIdx) => {
      const y = tableTop + 9 + rowIdx * rowHeight;
      const name = playerNames.get(pid) ?? '?';
      const isMe = pid === playerId;

      // Row background for local player
      if (isMe) {
        const rowBg = this.add.graphics();
        rowBg.fillStyle(0x3a2820, 0.6);
        rowBg.fillRect(tableLeft - 2, y - 1, nameColWidth + history.length * roundColWidth + 4, rowHeight);
      }

      // Player name (truncated)
      const displayName = name.length > 8 ? name.substring(0, 7) + '.' : name;
      const nameText = this.add.bitmapText(tableLeft, y, 'pixel', displayName.toUpperCase());
      nameText.setScale(1).setTint(isMe ? 0xd4a855 : 0xa08060);

      // Bet amounts per round
      history.forEach((round, colIdx) => {
        const x = tableLeft + nameColWidth + colIdx * roundColWidth;
        const bet = round.bets.find(b => b.playerId === pid);

        if (bet) {
          const wasEliminated = round.eliminatedPlayerId === pid;
          const betText = this.add.bitmapText(x + roundColWidth / 2, y, 'pixel', bet.amount.toString());
          betText.setOrigin(0.5, 0).setScale(1);
          betText.setTint(wasEliminated ? 0xff4444 : (isMe ? 0xf5e6d3 : 0xc0b0a0));
        } else {
          const dash = this.add.bitmapText(x + roundColWidth / 2, y, 'pixel', '.');
          dash.setOrigin(0.5, 0).setScale(1).setTint(0x4a3728);
        }
      });
    });

    // Stats summary
    const statsY = tableTop + 9 + allPlayerIds.length * rowHeight + 6;
    if (this.result.stats) {
      const statsText = this.add.bitmapText(width / 2, statsY, 'pixel',
        `${this.result.stats.totalRounds} ROUNDS`
      );
      statsText.setOrigin(0.5, 0).setScale(1).setTint(0x6a5a4a);
    }
  }

  private createConfetti(): void {
    const { width, height } = this.cameras.main;
    const colors = [0xd4a855, 0xf5e6d3, 0x8b7355, 0xc9a227, 0x6b8e23];

    for (let i = 0; i < 30; i++) {
      const x = Phaser.Math.Between(0, width);
      const delay = Phaser.Math.Between(0, 2500);
      const color = Phaser.Math.RND.pick(colors);
      const size = Phaser.Math.Between(1, 3);

      const particle = this.add.rectangle(x, -5, size, size, color);
      particle.setRotation(Phaser.Math.FloatBetween(0, Math.PI));

      this.tweens.add({
        targets: particle,
        y: height + 10,
        x: x + Phaser.Math.Between(-30, 30),
        rotation: particle.rotation + Phaser.Math.FloatBetween(2, 6),
        duration: Phaser.Math.Between(2500, 4500),
        delay,
        ease: 'Sine.easeIn',
        onComplete: () => particle.destroy(),
      });
    }
  }

  private setupSocketCallbacks(): void {
    socketManager.setCallbacks({
      onGameStarted: (gameState, yourMoney) => {
        this.registry.set('gameState', gameState);
        this.registry.set('yourMoney', yourMoney);

        this.cameras.main.fadeOut(300, 26, 20, 18);
        this.time.delayedCall(300, () => {
          this.scene.start('GameScene');
        });
      },
      onReturnedToLobby: (players, hostId) => {
        this.registry.set('players', players);
        this.registry.set('hostId', hostId);

        this.cameras.main.fadeOut(300, 26, 20, 18);
        this.time.delayedCall(300, () => {
          this.scene.start('LobbyScene');
        });
      },
      onError: (message) => {
        console.error('[Results] Error:', message);
      },
    });
  }
}
